/**
 * Извлекает выбранные query-параметры из URL/поисковой строки.
 *
 * Особенности:
 * - поддерживает вход `?a=1`, `a=1` и полный URL;
 * - декодирует percent-encoded ключи и значения;
 * - для повторяющихся ключей оставляет первое значение;
 * - не преобразует `+` в значениях в пробел, чтобы сохранять исходный символ `+`.
 *
 * @param url URL, `location.search` или "сырое" query-значение.
 * @param params Список имён параметров, которые нужно прочитать.
 */
export const getParamsFromUrl = <TParams extends readonly string[]>(
	url: string,
	params: TParams
): UrlParamsMap<TParams> => {
	const result: UrlParamsMap<TParams> = {};
	if (!url || params.length === 0) return result;

	const query = extractQueryString(url);
	if (!query) return result;

	const requestedParams = new Set<string>(params);
	const resultRecord = result as Record<string, string>;

	for (const pair of query.split('&')) {
		if (!pair) continue;

		const equalsIndex = pair.indexOf('=');
		const rawKey = equalsIndex === -1 ? pair : pair.slice(0, equalsIndex);
		const rawValue = equalsIndex === -1 ? '' : pair.slice(equalsIndex + 1);
		const decodedKey = decodeComponentSafely(rawKey.replaceAll('+', ' '));

		if (!requestedParams.has(decodedKey)) {
			continue;
		}

		if (decodedKey in resultRecord) {
			continue;
		}

		resultRecord[decodedKey] = decodeComponentSafely(rawValue);
	}

	return result;
};

const decodeComponentSafely = (value: string): string => {
	try {
		return decodeURIComponent(value);
	} catch {
		return value;
	}
};

/**
 * Возвращает query-часть из разных форм входной строки:
 * - `?a=1&b=2`
 * - `a=1&b=2`
 * - `https://example.com/path?a=1&b=2#hash`
 */
const extractQueryString = (url: string): string => {
	if (!url) return '';

	const hasSearchPrefix = url.startsWith('?');
	const queryStartIndex = url.indexOf('?');

	const queryLike = hasSearchPrefix
		? url.slice(1)
		: queryStartIndex === -1
			? url
			: url.slice(queryStartIndex + 1);

	const hashIndex = queryLike.indexOf('#');
	const query = hashIndex === -1 ? queryLike : queryLike.slice(0, hashIndex);

	if (!hasSearchPrefix && queryStartIndex === -1 && !query.includes('=') && !query.includes('&')) {
		return '';
	}

	return query;
};

export type UrlParamsMap<TParams extends readonly string[]> = Partial<
	Record<TParams[number], string>
>;
